import React from 'react';
import styled from 'styled-components';

const WhySection = styled.section`
  padding: 5rem 0;
  background: #f9fafb;
`;

const SectionTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 2.5rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 1rem;
  color: #000;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p`
  text-align: center;
  font-size: 1.1rem;
  color: #666;
  max-width: 800px;
  margin: 0 auto 4rem;
  line-height: 1.6;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FeatureCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  gap: 1.5rem;

  @media (max-width: 768px) {
    padding: 2rem;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  background: #eff6ff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const FeatureContent = styled.div`
  flex: 1;
  gap: 0.25rem;
`;

const FeatureHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
`;

const FeatureTitle = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: #000;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FeatureStat = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: #1d4ed8;
  white-space: nowrap;
  text-align: right;
  padding: 0.25rem;
  border: 1px solid #e5e5e5;
  border-radius: 5px;
  background-color: #f9fafb;
`;

const FeatureLabel = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
`;

const FeatureDescription = styled.p`
  color: #666;
  line-height: 1.6;
  margin: 0;
  font-size: 0.95rem;
`;

export default function WhyChooseUs() {
  return (
    <WhySection id="why-choose-us">
      <div className="container">
        <SectionTitle>Why residents choose Homero</SectionTitle>
        <SectionDescription>
          We've reimagined residential cleaning with trust, convenience, and
          transparency at the core.
        </SectionDescription>

        <FeaturesGrid>
          {/* Reclaim your time */}
          <FeatureCard>
            <FeatureContent>
              <FeatureHeader>
                <FeatureTitle>
                  <IconWrapper>🕐</IconWrapper>
                  <div
                    style={{
                      textAlign: 'left',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '0.25rem',
                    }}
                  >
                    Reclaim your time
                    <FeatureStat>8+ HOURS SAVED PER MONTH</FeatureStat>
                  </div>
                </FeatureTitle>
              </FeatureHeader>
              <FeatureDescription>
                Focus on living while we maintain your space to perfection. No
                compromises on quality.
              </FeatureDescription>
            </FeatureContent>
          </FeatureCard>

          {/* Vetted professionals only */}
          <FeatureCard>
            <FeatureContent>
              <FeatureHeader>
                <FeatureTitle>
                  <IconWrapper>🛡️</IconWrapper>
                  <div
                    style={{
                      textAlign: 'left',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '0.25rem',
                    }}
                  >
                    Vetted professionals only
                    <FeatureStat>100% BACKGROUND CHECKED</FeatureStat>
                  </div>
                </FeatureTitle>
              </FeatureHeader>
              <FeatureDescription>
                Homero cleaners are trusted employees, not anonymous contractors
                or temporary gig workers.
              </FeatureDescription>
            </FeatureContent>
          </FeatureCard>

          {/* Seamless building access */}
          <FeatureCard>
            <FeatureContent>
              <FeatureHeader>
                <FeatureTitle>
                  <IconWrapper>🏢</IconWrapper>
                  <div
                    style={{
                      textAlign: 'left',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '0.25rem',
                    }}
                  >
                    Seamless building access
                    <FeatureStat>100+ BUILDINGS</FeatureStat>
                  </div>
                </FeatureTitle>
              </FeatureHeader>
              <FeatureDescription>
                We coordinate with your building staff and management, so you
                never need to be home.
              </FeatureDescription>
            </FeatureContent>
          </FeatureCard>

          {/* Transparent pricing */}
          <FeatureCard>
            <FeatureContent>
              <FeatureHeader>
                <FeatureTitle>
                  <IconWrapper>💲</IconWrapper>
                  <div
                    style={{
                      textAlign: 'left',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '0.25rem',
                    }}
                  >
                    Transparent pricing
                    <FeatureStat>$26+ PER TASK</FeatureStat>
                  </div>
                </FeatureTitle>
              </FeatureHeader>
              <FeatureDescription>
                Know exactly what you'll pay upfront. No hidden fees, hourly
                minimums, or surprise charges.
              </FeatureDescription>
            </FeatureContent>
          </FeatureCard>
        </FeaturesGrid>
      </div>
    </WhySection>
  );
}
